import { allocate, Dinero, dinero, toDecimal, toSnapshot, transformScale } from "dinero.js"
import { TransformerOptions } from "@dinero.js/core"
import { Currency, DKK, EUR, GBP, NOK, SEK, USD } from "@dinero.js/currencies"
import { MonetaryAmount } from "./currencyTypes"

const SUPPORTED_CURRENCIES = [DKK, NOK, SEK, EUR, GBP, USD]

const currencyMap = SUPPORTED_CURRENCIES.reduce(
    (map, cur) => map.set(cur.code, cur),
    new Map() as Map<string, Currency<number>>,
)

export function toCurrency(currencyCode: string) {
    const currency = currencyMap.get(currencyCode)

    if (currency) {
        return currency
    } else {
        throw new Error(`Unsupported currency code: ${currencyCode}`)
    }
}

export function detectCurrencyFromDinero<TData>(
    data: TData[] | null | undefined,
    accessor: (item: TData) => Dinero<number> | null | undefined,
    fallback: Currency<number>,
) {
    if (!data) return fallback

    for (const item of data) {
        const amount = accessor(item)

        if (!amount) continue

        const { currency } = amount.toJSON()
        return currency
    }

    return fallback
}

export function detectCurrency<TData>(
    data: TData[] | null | undefined,
    accessor: (item: TData) => MonetaryAmount | null | undefined,
    fallback: Currency<number>,
) {
    if (!data) return fallback

    for (const item of data) {
        // NOTE: We simply use the first currency in the list since all items MUST have same currency in order to be summed, etc
        const amount = accessor(item)

        if (!amount) continue

        const currencyCode = amount?.currency
        return currencyCode ? toCurrency(currencyCode) : fallback
    }

    return fallback
}

export function isDinero(value: unknown): value is Dinero<number> {
    return (
        typeof value === "object" &&
        value !== null &&
        "calculator" in value &&
        "formatter" in value &&
        "toJSON" in value
    )
}

export function zeroDinero(currency: Currency<number>) {
    return dinero({ amount: 0, scale: currency.exponent, currency })
}

export function toDinero(monetaryAmount: MonetaryAmount) {
    const { amount, scale, currency: code } = monetaryAmount
    const currency = toCurrency(code)
    return dinero({ amount, scale, currency })
}

export function toDineroOrUndefined(monetaryAmount: MonetaryAmount | null | undefined) {
    return monetaryAmount ? toDinero(monetaryAmount) : undefined
}

export function toDineroOrZero(
    monetaryAmount: MonetaryAmount | null | undefined,
    currency: Currency<number> = toCurrency("DKK"),
) {
    return toDineroOrUndefined(monetaryAmount) ?? zeroDinero(currency)
}

export function toDineroLossy(value: number, currency: Currency<number>) {
    if (typeof currency.base === "number") {
        const factor = currency.base ** currency.exponent
        const amount = Math.round(value * factor)

        return dinero({ amount: amount, currency })
    } else {
        throw new Error(`Unsupported currency (multiple subdivisions not supported): ${currency.code}`)
    }
}

export function toMonetaryAmount(dinero: Dinero<number>) {
    const {
        amount,
        scale,
        currency: { code: currencyCode },
    } = toSnapshot(dinero)

    const monetaryAmount: MonetaryAmount = {
        amount,
        scale,
        currency: currencyCode,
    }

    return monetaryAmount
}

export function toNumberLossy(value: Dinero<number>) {
    return toDecimalParts(value).value
}

// Formatting needs the decimal value and the fraction digits; this is the one place a Dinero is
// taken apart for that, so MoneyFactory stays free of toJSON/toSnapshot.
export function toDecimalParts(value: Dinero<number>) {
    const { amount, scale } = toSnapshot(value)
    return { value: amount / Math.pow(10, scale), scale }
}

export function getCurrency(value: Dinero<number>) {
    return toSnapshot(value).currency
}

// Stable identity of a MonetaryAmount for React keys and de-duplication — the parts are read here
// rather than in feature code.
export function monetaryAmountKey({ amount, scale, currency }: MonetaryAmount) {
    return `${amount}:${scale}:${currency}`
}

export function getIncludedVAT(totalPrice: Dinero<number>) {
    const dkVatRate = 25 // TODO: Need to take from response when available
    const [, vat] = allocate(totalPrice, [100 - dkVatRate, dkVatRate])
    return vat
}

export function roundToCurrency(value: Dinero<number>) {
    const {
        currency: { exponent: currencyScale },
    } = toSnapshot(value)
    return transformScale(value, currencyScale)
}

/**
 * Rounds to the nearest multiple of `fractionOfUnit` major units (.25 -> nearest 25 oere).
 * Lives here because this file is the only place allowed to read the raw amount off a Dinero.
 */
export function roundToNearestFraction(value: Dinero<number>, fractionOfUnit: number) {
    const rounded = roundToCurrency(value)
    const { amount, scale, currency } = toSnapshot(rounded)
    const step = Math.round(fractionOfUnit * Math.pow(10, scale))

    if (step <= 1) return rounded

    return dinero({ amount: Math.round(amount / step) * step, currency, scale })
}

export function createMoneyFormatter(locale: string) {
    function transformer({ value, currency }: TransformerOptions<number, string>) {
        return Number(value).toLocaleString(locale, {
            style: "currency",
            currency: currency.code,
        })
    }

    return function formatter(value: Dinero<number>) {
        return toDecimal(value, transformer)
    }
}

export function createRoundingMoneyFormatter(locale: string) {
    const formatter = createMoneyFormatter(locale)

    return function roundingFormatter(value: Dinero<number>) {
        const rounded = roundToCurrency(value)
        return formatter(rounded)
    }
}

function isCurrencyPart(partType: string) {
    return partType === "code" || partType === "currency" || partType === "literal" || partType === "symbol"
}

export function createRoundingNumberFormatter(locale: string, scale: "currency" | "value" = "currency") {
    function transformer({ value, currency }: TransformerOptions<number, string>) {
        const numberFormat = Intl.NumberFormat(locale, {
            style: "currency",
            currency: currency.code,
        })
        const result = numberFormat
            .formatToParts(Number(value))
            .filter((part) => !isCurrencyPart(part.type))
            .map((part) => part.value)
            .join("")
        return result
    }

    if (scale === "currency") {
        return function currencyRoundingFormatter(value: Dinero<number>) {
            const rounded = roundToCurrency(value)
            return toDecimal(rounded, transformer)
        }
    } else {
        return function valueRoundingFormatter(value: Dinero<number>) {
            const { scale } = toSnapshot(value)

            function transformer({ value, currency }: TransformerOptions<number, string>) {
                const numberFormat = Intl.NumberFormat(locale, {
                    style: "currency",
                    currency: currency.code,
                    maximumFractionDigits: scale,
                })

                const result = numberFormat
                    .formatToParts(Number(value))
                    .filter((part) => !isCurrencyPart(part.type))
                    .map((part) => part.value)
                    .join("")

                return result
            }

            return toDecimal(value, transformer)
        }
    }
}
